'use client';

import type { MouseEvent as ReactMouseEvent } from 'react';

import type { ImageZoomApi } from './use-image-zoom';
import { AttachmentLightboxImage } from './attachment-lightbox-image';
import { PdfScrollView } from './pdf-scroll-view';
import { YouTubeEmbed, getYouTubeVideoId } from './youtube-embed';
import { TextAttachmentView } from './text-attachment-view';
import { VideoPlayer } from './video-player';
// Deep-import the concrete module, not the `@naumu/shared` barrel - the barrel
// re-exports every schemas/*.ts and drags zod (~299 KiB) into any chunk that
// touches it, including the marketing shell's.
import { isImageType, isVideoType, isPdfType, isTextType } from '@naumu/shared/types/attachment';
import type { LightboxAttachment } from './attachment-lightbox';

interface AttachmentLightboxSlideProps {
	attachment: LightboxAttachment;
	/** The single shared zoom instance — its ref is bound only on the active slide. */
	zoom: ImageZoomApi;
	/** This slide is the one currently in view. */
	isActive: boolean;
	/** Within the render window (|i - current| <= 1). Outside it, heavy media is unmounted. */
	isMounted: boolean;
	isMobile: boolean;
	onContextMenu: (e: ReactMouseEvent) => void;
	/** Close when the empty area of the slide (not the media) is clicked. */
	onBackdropClick: () => void;
}

/**
 * One carousel slide. Occupies exactly the same centered box as the pre-carousel
 * lightbox content (media keeps its `max-h-[85dvh] max-w-[90vw]` sizing). Heavy
 * content only mounts inside the render window; videos mount only while active so
 * swiping away stops playback.
 */
export function AttachmentLightboxSlide({
	attachment,
	zoom,
	isActive,
	isMounted,
	isMobile,
	onContextMenu,
	onBackdropClick,
}: AttachmentLightboxSlideProps) {
	const youTubeId = getYouTubeVideoId(attachment.url);

	return (
		<div
			className="flex h-full w-full shrink-0 snap-center items-center justify-center"
			onClick={(e) => {
				if (e.target === e.currentTarget) onBackdropClick();
			}}
		>
			{!isMounted ? null : youTubeId ? (
				<YouTubeEmbed
					videoId={youTubeId}
					title={attachment.fileName}
					className="w-[min(90vw,calc(85dvh*16/9))]"
				/>
			) : isImageType(attachment.fileType) ? (
				<AttachmentLightboxImage
					attachment={attachment}
					zoom={zoom}
					isActive={isActive}
					onContextMenu={onContextMenu}
				/>
			) : isVideoType(attachment.fileType) ? (
				// Mount the video only while active: autoPlay on entry, unmount on exit
				// so audio never plays from an off-screen slide.
				isActive ? (
					// VideoPlayer has no onContextMenu prop, so the wrapper carries it.
					// Uploads are arbitrary ratios: `aspect="native"` lets the frame take
					// the file's own ratio (letterboxed until metadata arrives, never
					// cropped) and `maxHeight` keeps a tall clip inside the shared
					// `max-h-[85dvh] max-w-[90vw]` media box - the player derives the
					// matching width itself.
					<div onContextMenu={onContextMenu} className="w-[90vw]">
						<VideoPlayer
							variant="inline"
							aspect="native"
							maxHeight="85dvh"
							src={attachment.url}
							width={1920}
							height={1080}
							autoPlay
							title={attachment.fileName}
							className="w-full"
						/>
					</div>
				) : null
			) : isPdfType(attachment.fileType) ? (
				isMobile ? (
					<PdfScrollView
						url={attachment.url}
						fileName={attachment.fileName}
						className="h-[85dvh] w-[92vw]"
					/>
				) : (
					<iframe
						src={attachment.url}
						title={attachment.fileName}
						className="h-[85dvh] w-[80vw] rounded-lg bg-background"
					/>
				)
			) : isTextType(attachment.fileType) ? (
				<TextAttachmentView url={attachment.url} fileName={attachment.fileName} />
			) : null}
		</div>
	);
}
